import api from "@/app/api";
import { filterAndSearchProducts } from "@/lib/search";
import type { ProductResponse } from "@/app/api/types/product";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import type { UUID } from "node:crypto";
import { productKeys } from "../keys/products";
import type { ProductFilters } from "../types";
import { useGlobalProducts } from "./useUnifiedCache";

/**
 * THE MASTER PRODUCT QUERY - Uses unified global cache with client-side filtering
 * This replaces the old infinite scroll pagination with a single global cache
 * All filtering and search happens client-side using the search engine
 *
 * @param filters - Filtering options (search, labels, includeSleeping, sleepingOnly, includeAll)
 * @param pageSize - Deprecated, kept for backward compatibility
 */
export function useAllProducts(
  filters: ProductFilters = {},
  pageSize: number = 750,
) {
  // Load global products cache (loads all products once, then polls every 5min)
  // Pass includeAll to show all products, not just user's attached products
  const globalQuery = useGlobalProducts({ includeAll: filters.includeAll });

  // Apply client-side filtering and search
  const filteredData = useMemo(() => {
    if (!globalQuery.data) return [];

    const products = globalQuery.data as ProductResponse[];

    // Apply filtering and search using the search engine
    return filterAndSearchProducts(products, {
      search: filters.search,
      labels: filters.labels,
      includeSleeping: filters.includeSleeping ?? false,
      sleepingOnly: filters.sleepingOnly ?? false,
    });
  }, [
    globalQuery.data,
    filters.search,
    filters.labels,
    filters.includeSleeping,
    filters.sleepingOnly,
  ]);

  // For backward compatibility, return the same interface as before
  return {
    data: filteredData,
    total: filteredData.length,
    isCacheComplete: true, // Always complete since we load all data
    isLoading: globalQuery.isLoading,
    isError: globalQuery.isError,
    error: globalQuery.error,
    isFetching: globalQuery.isFetching,
    refetch: globalQuery.refetch,
    // Deprecated infinite scroll props (no-ops for backward compatibility)
    fetchNextPage: async () => {},
    hasNextPage: false,
    isFetchingNextPage: false,
  };
}

/**
 * UNIFIED Products Hook - Now uses global cache with client-side filtering
 *
 * Note: Some legacy parameters (include_children, exclude_variants, sort_by, sort_order)
 * are no longer supported since we use client-side filtering.
 * The hook is maintained for backward compatibility but simplified.
 *
 * Supported parameters:
 * - search: Text search query
 * - include_sleeping: Include sleeping products
 * - sleeping_only: Only sleeping products
 *
 * Example usage:
 * ```
 * // Get all products
 * const { data, isLoading } = useProducts();
 *
 * // Search products
 * const { data } = useProducts({ search: "apple" });
 *
 * // Get sleeping products only
 * const { data } = useProducts({ sleeping_only: true });
 * ```
 */
export function useProducts(
  params: {
    search?: string;
    include_children?: boolean;
    exclude_variants?: boolean;
    include_sleeping?: boolean;
    sleeping_only?: boolean;
    user_products_only?: boolean;
    sort_by?: string;
    sort_order?: string;
    pageSize?: number;
    infiniteScroll?: boolean;
    includeAll?: boolean; // If true, returns all products without user filtering
    /**
     * When false, no catalog subscription is taken and `data` is `[]`.
     * For callers that already hold the catalog from a hoisted provider (e.g.
     * recap's `ProductLookupsContext`) and only mount this hook to keep hook
     * order stable. Forwards to `useGlobalProducts`, which supports it.
     */
    enabled?: boolean;
  } = {},
) {
  const {
    search,
    include_sleeping = false,
    sleeping_only = false,
    includeAll,
    enabled = true,
  } = params;

  // Load global products cache
  const globalQuery = useGlobalProducts({ includeAll, enabled });

  // Apply client-side filtering
  const filteredData = useMemo(() => {
    if (!globalQuery.data) return [];

    const products = globalQuery.data as ProductResponse[];

    return filterAndSearchProducts(products, {
      search,
      includeSleeping: include_sleeping,
      sleepingOnly: sleeping_only,
    });
  }, [globalQuery.data, search, include_sleeping, sleeping_only]);

  // Return interface compatible with old hook
  return {
    data: filteredData,
    total: filteredData.length,
    isLoading: globalQuery.isLoading,
    isError: globalQuery.isError,
    error: globalQuery.error,
    isFetching: globalQuery.isFetching,
    refetch: globalQuery.refetch,
    // Deprecated props for backward compatibility
    fetchNextPage: async () => {},
    hasNextPage: false,
    isFetchingNextPage: false,
  };
}

/**
 * Individual product query - uses global cache when available
 */
export function useProduct(id: string) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: productKeys.detail(id),
    queryFn: async () => {
      // First try to get from global products cache
      const globalCache = queryClient.getQueryData<ProductResponse[]>([
        "products",
        "global",
      ]);

      if (globalCache) {
        const cached = globalCache.find((p) => p.id === id);
        if (cached) {
          return cached;
        }
      }

      // Fallback to API if not in cache
      return api.products.getProductById(id as UUID);
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!id,
  });
}

/**
 * Product lookup by SKU - uses global cache
 */
export function useProductBySku(sku: string) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: productKeys.bySku(sku),
    queryFn: async () => {
      // Try to find in global cache first
      const globalCache = queryClient.getQueryData<ProductResponse[]>([
        "products",
        "global",
      ]);

      if (globalCache) {
        const cached = globalCache.find((p) => p.sku === sku);
        if (cached) {
          return cached;
        }
      }

      // Fallback to API
      return api.products.getProductBySku(sku);
    },
    staleTime: 10 * 60 * 1000, // Longer cache for SKU lookups
    enabled: !!sku,
  });
}

/**
 * Product lookup by code - uses global cache
 */
export function useProductByCode(code: string) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: productKeys.byCode(code),
    queryFn: async () => {
      // Try to find in global cache first
      const globalCache = queryClient.getQueryData<ProductResponse[]>([
        "products",
        "global",
      ]);

      if (globalCache) {
        const cached = globalCache.find((p) => p.code === code);
        if (cached) {
          return cached;
        }
      }

      // Fallback to API
      return api.products.getProductByCode(code);
    },
    staleTime: 10 * 60 * 1000,
    enabled: !!code,
  });
}

/**
 * Products for a specific user
 */
export function useUserProducts(userId: string) {
  return useQuery({
    queryKey: productKeys.userProducts(userId),
    queryFn: () => api.users.getUserProducts(userId as UUID),
    staleTime: 5 * 60 * 1000,
    enabled: !!userId,
  });
}

// Note: Product members endpoint doesn't exist in backend
// This would need to be implemented in the backend first

/**
 * Hook to check if any product queries are loading
 * Useful for global loading states
 */
export function useProductsStatus() {
  const queryClient = useQueryClient();

  const allProductQueries = queryClient
    .getQueryCache()
    .findAll({ queryKey: productKeys.all });

  const isLoading = allProductQueries.some((q) => q.state.status === "pending");
  const hasError = allProductQueries.some((q) => q.state.status === "error");
  const isFetching = allProductQueries.some(
    (q) => q.state.fetchStatus === "fetching",
  );

  return { isLoading, hasError, isFetching };
}

/**
 * Fetches recency-weighted product relevance scores.
 * Loads independently/in parallel — never blocks product selection.
 * Returns a Map<productId, score> for O(1) lookups.
 */
export function useProductRelevance() {
  const query = useQuery({
    queryKey: productKeys.relevance(),
    queryFn: () => api.products.getProductRelevance(30),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const relevanceMap = useMemo(() => {
    const map = new Map<string, number>();
    if (query.data) {
      for (const item of query.data) {
        map.set(item.product_id, item.score);
      }
    }
    return map;
  }, [query.data]);

  return { data: relevanceMap, isLoading: query.isLoading };
}
